// ─── Stats ───────────────────────────────────────────────────────────────────
import dayjs from "dayjs";
import { sanitizeText } from "commons/helper";

export function getCommunityStatCards(stats) {
  return [
    { label: "Active members", value: stats?.active_members },
    { label: "Events hosted", value: stats?.total_events_hosted },
    { label: "Upcoming events", value: stats?.num_upcoming_events },
    { label: "Lapsed members", value: stats?.expired_members },
  ];
}

// ─── Form payload ─────────────────────────────────────────────────────────────

export function buildCommunityPayload(values, locations) {
  const phoneNumbers = (values.phone_numbers ?? []).map((p) =>
    p.trim().replace(/\D/g, "")
  );

  const ownerMetadata = (values.owner_metadata ?? [])
    .filter((o) => o.name?.trim())
    .map((o) => ({
      name: sanitizeText(o.name),
      hosting_since: dayjs(o.hosting_since).format("YYYY-MM-DD"),
      ...(o.phone_numbers?.trim()
        ? {
            phone_numbers: o.phone_numbers.trim().replace(/\D/g, "").slice(-15),
          }
        : {}),
      ...(sanitizeText(o.description)
        ? { description: sanitizeText(o.description) }
        : {}),
      ...(o.logo_image_url?.trim()
        ? { logo_image_url: o.logo_image_url.trim() }
        : {}),
    }));

  const tcTitle = sanitizeText(values.tc_title);
  const tcSubTitle = sanitizeText(values.tc_sub_title);
  const tcSections = (values.tc_sections ?? []).filter(
    (s) => s?.heading?.trim() && s?.content?.trim()
  );
  return {
    name: sanitizeText(values.name),
    ...(values.display_name && {
      display_name: sanitizeText(values.display_name),
    }),
    ...(values.description && {
      description: sanitizeText(values.description),
    }),
    ...(values.logo_url && { logo_url: values.logo_url }),
    ...(values.cover_url && { cover_url: values.cover_url }),
    operational_locations: locations
      .filter((l) => l.city_code && l.area_codes?.length > 0)
      .map((l) => ({
        city_code: l.city_code,
        area_codes: l.area_codes,
      })),
    contact_details: {
      phone_numbers: phoneNumbers,
      ...(values.contact_emails?.length && {
        email: values.contact_emails,
      }),
      ...(values.contact_websites?.length && {
        website: values.contact_websites,
      }),
    },
    owner_metadata: ownerMetadata,
    ...(values.collection_tags?.length && {
      collection_tags: values.collection_tags,
    }),
    ...(values.activities?.length && { activities: values.activities }),
    ...(values.what_we_do?.length && { what_we_do: values.what_we_do }),
    ...(tcTitle &&
      tcSubTitle &&
      tcSections?.length && {
        terms_and_conditions: {
          title: tcTitle,
          sub_title: tcSubTitle,
          sections: tcSections.map((s) => ({
            heading: sanitizeText(s.heading),
            content: sanitizeText(s.content),
          })),
        },
      }),
    ...(values.is_active !== undefined ? { is_active: values.is_active } : {}),
  };
}

export function getDefaultFormValues(community) {
  if (!community) return { owner_metadata: [{ name: "" }] };
  return {
    name: community.name,
    display_name: community.display_name,
    description: community.description,
    logo_url: community.logo_url,
    cover_url: community.cover_url,
    activities: community.activities ?? [],
    what_we_do: community.what_we_do ?? [],
    phone_numbers: community.contact_details?.phone_numbers ?? [],
    contact_emails: (community.contact_details?.email ?? []),
    contact_websites: (community.contact_details?.website ?? []),
    owner_metadata: (community.owner_metadata ?? []).map((o) => ({
      name: o.name ?? "",
      phone_numbers: o.phone_numbers ?? "",
      description: o.description ?? "",
      hosting_since: o?.hosting_since ? dayjs(o.hosting_since) : undefined,
      logo_image_url: o.logo_image_url ?? "",
    })),
    collection_tags: community.collection_tags ?? [],
    tc_title: community.terms_and_conditions?.title,
    tc_sub_title: community.terms_and_conditions?.sub_title,
    tc_sections: community.terms_and_conditions?.sections ?? [],
    is_active: community.is_active ?? true,
  };
}

export function getDefaultLocations(community) {
  if (!community?.operational_locations?.length) {
    return [{ city_code: "", area_codes: [] }];
  }
  return community.operational_locations.map((loc) => ({
    city_code: loc.city_code ?? "",
    area_codes: loc.area_codes ?? [],
  }));
}
